import axios from 'axios';
import { API_URL } from '../config/config';
import { getHeaderOptions } from './getHeaderOptions';
import logger from '../utils/logger';

const API_ENDPOINT = `${API_URL}api/v1`;
logger.log('🚀 Enhanced BotService: API Endpoint ->', API_ENDPOINT);

// Simple interfaces based on your form
export interface Bot {
  metrics: any;
  _id: string;
  name: string;
  description?: string; // 🔥 FIX: Add description field
  objective?: string;
  status: 'draft' | 'active' | 'inactive';
  botType?: string;
  triggerCondition?: {
    keywords?: string[];
    requireAllKeywords?: boolean;
  };
  groups?: any[];
  createdAt?: string;
  lastTriggerAt?: string;
}

export interface BotListParams {
  search?: string;
  page?: number;
  perPage?: number;
  orderBy?: string;
  isAscending?: boolean;
}

// What your form actually sends
export interface FormPayload {
  name: string;
  objective?: string;
  triggerCondition: {
    type: 'keyword';
    keywords: string[];
    requireAllKeywords?: boolean;
  };
  status: 'active' | 'inactive';
  groups: Array<{
    id: string;
    groupName: string;
    required: boolean;
    conditions: {};
    steps: Array<{
      id: string;
      fieldType: string;
      message: null;
      validation: {};
      metadata: {
        templateId: string;
        templateName: string;
      };
      options: {};
    }>;
  }>;
}

// Get headers with auth token
const getHeaders = () => {
  const token = localStorage.getItem("auth_token") || "";
  logger.log('🔑 Headers: Token exists ->', !!token);
  
  return {
    ...getHeaderOptions,
    Authorization: token,
  };
};

/**
 * 📋 GET ALL BOTS
 */
export const getBotList = async (params: BotListParams) => {
  logger.log('📋 GET BOTS: Starting with params ->', params);
  
  try {
    const queryParams = new URLSearchParams({
      page: params.page?.toString() || '1',
      perPage: params.perPage?.toString() || '15',
      orderBy: params.orderBy || '_id',
      isAscending: params.isAscending?.toString() || 'false',
      ...(params.search && { search: params.search })
    });

    const url = `${API_ENDPOINT}/bot?${queryParams}`;
    logger.log('🔗 GET BOTS: URL ->', url);

    const response = await axios.get(url, { headers: getHeaders() });
    
    logger.log('✅ GET BOTS: Success ->', {
      count: response.data.data?.length,
      total: response.data.total
    });

    return response.data;
  } catch (error: any) {
    console.error('❌ GET BOTS: Failed ->', error.response?.data || error.message);
    throw error;
  }
};

/**
 * 🔍 GET BOT BY ID - NEW FUNCTION
 */
export const getBotById = async (botId: string) => {
  logger.log('🔍 GET BOT BY ID: Starting for ID ->', botId);
  
  try {
    const url = `${API_ENDPOINT}/bot/${botId}`;
    logger.log('🔗 GET BOT BY ID: URL ->', url);

    const response = await axios.get(url, { headers: getHeaders() });
    
    logger.log('✅ GET BOT BY ID: Success ->', {
      id: response.data.data._id,
      name: response.data.data.name,
      status: response.data.data.status
    });

    return response.data;
  } catch (error: any) {
    console.error('❌ GET BOT BY ID: Failed ->', error.response?.data || error.message);
    console.error('💥 GET BOT BY ID: Failed for ID ->', botId);
    throw error;
  }
};

/**
 * ✨ CREATE NEW BOT
 */
export const createBot = async (data: FormPayload) => {
  logger.log('✨ CREATE BOT: Starting with data ->', {
    name: data.name,
    keywords: data.triggerCondition.keywords,
    status: data.status,
    templateId: data.groups[0]?.steps[0]?.metadata?.templateId
  });
  
  try {
    const url = `${API_ENDPOINT}/bot`;
    logger.log('🔗 CREATE BOT: URL ->', url);
    logger.log('📦 CREATE BOT: Full payload ->', JSON.stringify(data, null, 2));

    const response = await axios.post(url, data, { headers: getHeaders() });
    
    logger.log('✅ CREATE BOT: Success ->', {
      id: response.data.data._id,
      name: response.data.data.name,
      status: response.data.data.status
    });

    return response.data.data;
  } catch (error: any) {
    console.error('❌ CREATE BOT: Failed ->', error.response?.data || error.message);
    console.error('💥 CREATE BOT: Failed payload was ->', data);
    throw error;
  }
};

/**
 * 🔄 UPDATE EXISTING BOT
 */
export const updateBot = async (id: string, data: Partial<FormPayload>) => {
  logger.log('🔄 UPDATE BOT: Starting ->', {
    id,
    name: data.name,
    keywords: data.triggerCondition?.keywords,
    status: data.status
  });
  
  try {
    const url = `${API_ENDPOINT}/bot/${id}`;
    logger.log('🔗 UPDATE BOT: URL ->', url);
    logger.log('📦 UPDATE BOT: Payload ->', JSON.stringify(data, null, 2));

    const response = await axios.put(url, data, { headers: getHeaders() });
    
    logger.log('✅ UPDATE BOT: Success ->', {
      id: response.data.data._id,
      name: response.data.data.name,
      status: response.data.data.status
    });

    return response.data.data;
  } catch (error: any) {
    console.error('❌ UPDATE BOT: Failed ->', error.response?.data || error.message);
    console.error('💥 UPDATE BOT: Failed for ID ->', id);
    throw error;
  }
};

/**
 * 🗑️ DELETE BOT
 */
export const deleteBot = async (id: string) => {
  logger.log('🗑️ DELETE BOT: Starting for ID ->', id);
  
  try {
    const url = `${API_ENDPOINT}/bot/${id}`;
    logger.log('🔗 DELETE BOT: URL ->', url);

    await axios.delete(url, { headers: getHeaders() });
    
    logger.log('✅ DELETE BOT: Success for ID ->', id);
  } catch (error: any) {
    console.error('❌ DELETE BOT: Failed ->', error.response?.data || error.message);
    console.error('💥 DELETE BOT: Failed for ID ->', id);
    throw error;
  }
};

/**
 * 🔄 UPDATE BOT STATUS ONLY
 */
export const updateBotStatus = async (id: string, status: 'draft' | 'active' | 'inactive') => {
  logger.log('🔄 UPDATE STATUS: Starting ->', { id, status });
  
  try {
    const url = `${API_ENDPOINT}/bot/${id}`;
    logger.log('🔗 UPDATE STATUS: URL ->', url);

    const response = await axios.put(url, { status }, { headers: getHeaders() });
    
    logger.log('✅ UPDATE STATUS: Success ->', {
      id: response.data.data._id,
      newStatus: response.data.data.status
    });

    return response.data.data;
  } catch (error: any) {
    console.error('❌ UPDATE STATUS: Failed ->', error.response?.data || error.message);
    console.error('💥 UPDATE STATUS: Failed for ->', { id, status });
    throw error;
  }
};

/**
 * 📋 COPY BOT - Creates a duplicate of an existing bot
 */
export const copyBot = async (id: string) => {
  logger.log('📋 COPY BOT: Starting for ID ->', id);
  
  try {
    // First get the bot data
    const getBotResponse = await getBotById(id);
    const originalBot = getBotResponse.data;
    
    // Create copy payload with modified name and draft status
    const copyPayload: FormPayload = {
      name: `${originalBot.name} (Copy)`,
      objective: originalBot.objective,
      triggerCondition: originalBot.triggerCondition,
      status: 'inactive', // New bots start as inactive
      groups: originalBot.groups
    };
    
    logger.log('📋 COPY BOT: Creating copy with payload ->', {
      originalName: originalBot.name,
      newName: copyPayload.name,
      status: copyPayload.status
    });

    // Create the new bot
    const newBot = await createBot(copyPayload);
    
    logger.log('✅ COPY BOT: Success ->', {
      originalId: id,
      newId: newBot._id,
      newName: newBot.name
    });

    return newBot;
  } catch (error: any) {
    console.error('❌ COPY BOT: Failed ->', error.response?.data || error.message);
    console.error('💥 COPY BOT: Failed for ID ->', id);
    throw error;
  }
};